import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchX } from 'lucide-react';
import type {
  ContactTimelineEvent,
  ContactTimelineEventType,
  ContactTimelineMatchedContact,
  ContactTimelineResult,
  ContactTimelineSummary,
} from '@/dashboard/types';
import { fetchOwnerContactTimeline } from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Formats an ISO timestamp like "2026-05-15T15:42:00.000Z" into the
 * human-readable copy shown next to each timeline event. Pure helper
 * exported for unit tests; the implementation defers to the runtime's
 * `toLocaleString` so the test asserts only on the parts that are
 * stable across locales (year + month + day at minimum).
 *
 * Falsy/invalid input falls back to the original string so we never
 * crash the page on a malformed `at` value from the backend.
 */
export function formatEventTimestamp(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Maps a raw event type identifier (e.g. `walkin_registered`) into the
 * operator-facing label shown in the timeline's Type column. Special
 * cases are listed up front so product copy can be tweaked without
 * touching the rendering code; unknown event types fall through to a
 * generic snake_case → Sentence-case transform so newly-introduced
 * backend types degrade gracefully instead of leaking machine names.
 *
 * Pure function. Case-insensitive on input. Returns an empty string
 * when called with an empty string so callers can render nothing
 * rather than the literal text "Unknown event". This mirrors the
 * `formatEventTimestamp` empty-string contract above.
 */
export function formatEventTypeLabel(eventType: string): string {
  if (!eventType) return '';

  const SPECIAL_LABELS: Record<string, string> = {
    walkin_registered: 'Walk-in registered',
    attribution_recorded: 'Source captured',
    whatsapp_sent: 'WhatsApp sent',
    whatsapp_received: 'WhatsApp received',
    contact_created: 'Contact created',
  };

  const key = eventType.toLowerCase();
  if (key in SPECIAL_LABELS) return SPECIAL_LABELS[key] as string;

  const spaced = key.replace(/_/g, ' ').trim();
  if (spaced.length === 0) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Maps an event type to one of the existing badge variant classes.
 * Stays inside the dashboard.css palette (badge / badge--warning /
 * badge--danger) — no new tokens. Inbound/outbound messages share the
 * plain `badge` colour; admin-side events (contact created, walkin
 * registered, attribution recorded) carry `badge--warning` so they
 * read as "operator should notice" without escalating to danger.
 */
export function eventTypeBadgeClass(type: ContactTimelineEventType): string {
  switch (type) {
    case 'contact_created':
    case 'walkin_registered':
    case 'attribution_recorded':
      return 'badge badge--warning';
    case 'message_inbound':
    case 'message_outbound':
      return 'badge';
    default:
      return 'badge';
  }
}

/**
 * Humanises a backend-issued summary line for display in the timeline.
 *
 * Backend ships compact, machine-style summaries like
 *   `Walk-in registered at Shilpavally (walk_in)`
 *   `Attribution: walkin_form/walk_in`
 * because they're cheap to assemble in SQL. The clinic-facing UI
 * reads better when the channel suffix is stripped (the event type
 * badge already encodes "walk-in") and when the `Attribution: a/b`
 * pattern is rewritten into a natural-language phrase keyed off the
 * known source / channel taxonomy.
 *
 * Pure function. Falls through to the original `event.summary` (with
 * leading / trailing whitespace trimmed) for anything outside the
 * known patterns so newly-introduced backend summary shapes degrade
 * gracefully rather than vanishing.
 */
export function formatEventSummary(event: {
  summary?: string | null;
}): string {
  const raw = (event.summary ?? '').trim();
  if (raw.length === 0) return '';

  const ATTR_LABELS: Record<string, string> = {
    walkin_form: 'walk-in form',
    walk_in: 'walk-in',
    walkin: 'walk-in',
    lead_form: 'lead form',
    referral: 'a referral',
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    facebook: 'Facebook',
    google: 'Google',
    organic: 'organic search',
  };

  const attr = raw.match(
    /^attribution\s*:\s*([a-z0-9_]+)(?:\s*\/\s*([a-z0-9_]+))?/i,
  );
  if (attr) {
    const source = (attr[1] ?? '').toLowerCase();
    const channel = (attr[2] ?? '').toLowerCase();
    const label = ATTR_LABELS[source] ?? ATTR_LABELS[channel];
    return label ? `Source captured from ${label}` : 'Source captured';
  }

  const stripped = raw.replace(/\s*\(\s*[a-z0-9_-]+\s*\)\s*$/i, '').trim();
  return stripped.length > 0 ? stripped : raw;
}

function MatchedContactCard({
  contact,
}: {
  contact: ContactTimelineMatchedContact;
}): JSX.Element {
  const name = contact.patientName ?? 'Unnamed contact';
  const clinic = contact.clinicName ?? '(unknown clinic)';
  const status = contact.convertedToPatient
    ? 'Converted to patient'
    : 'Not yet converted';
  return (
    <Card data-testid="contact-card" size="sm">
      <CardHeader>
        <CardTitle>{name}</CardTitle>
        <CardDescription>{clinic}</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          {contact.age !== null ? (
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Age
              </dt>
              <dd className="mt-0.5 font-medium">{contact.age}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Visits
            </dt>
            <dd className="mt-0.5 font-medium">{contact.totalVisits}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Status
            </dt>
            <dd className="mt-0.5 font-medium">{status}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

function EmptyTimelineState(): JSX.Element {
  return (
    <div
      data-testid="empty-timeline"
      className="flex flex-col items-center justify-center gap-3 py-12 text-center"
    >
      <SearchX className="size-10 text-muted-foreground" aria-hidden="true" />
      <h2 className="text-base font-medium">No activity found</h2>
      <p className="max-w-xs text-sm text-muted-foreground">
        Try a different phone number, or check the format (10 digits).
      </p>
    </div>
  );
}

function EventDetails({ event }: { event: ContactTimelineEvent }): JSX.Element | null {
  const payloadKeys = Object.keys(event.payload ?? {});
  if (payloadKeys.length === 0) return null;
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-xs text-muted-foreground">
        Technical details
      </summary>
      <pre className="mt-2 overflow-x-auto rounded bg-muted px-2 py-1.5 font-mono text-xs">
        {JSON.stringify(event.payload, null, 2)}
      </pre>
    </details>
  );
}

function EventDesktopRow({ event }: { event: ContactTimelineEvent }): JSX.Element {
  return (
    <tr>
      <td>
        <span className={eventTypeBadgeClass(event.type)}>
          {formatEventTypeLabel(event.type)}
        </span>
      </td>
      <td>{formatEventTimestamp(event.at)}</td>
      <td>{event.clinicName ?? '(unknown clinic)'}</td>
      <td>
        <div>{formatEventSummary(event)}</div>
        <EventDetails event={event} />
      </td>
    </tr>
  );
}

function EventMobileCard({ event }: { event: ContactTimelineEvent }): JSX.Element {
  return (
    <Card size="sm" data-testid="event-mobile-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <span className={eventTypeBadgeClass(event.type)}>
            {formatEventTypeLabel(event.type)}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatEventTimestamp(event.at)}
          </span>
        </div>
        <CardDescription>
          {event.clinicName ?? '(unknown clinic)'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-sm">{formatEventSummary(event)}</div>
        <EventDetails event={event} />
      </CardContent>
    </Card>
  );
}

/**
 * Pure presentational view for the contact timeline page. Receives a
 * fully-resolved result and renders the matched-contact card(s), the
 * event list, and the summary footer. Exported so tests can render
 * it with controlled props without exercising the data-fetching
 * parent (mirrors the `TopIssuesPanel` pattern on the owner page).
 *
 * Responsive: the event list reflows from a table (md and above) to
 * a stack of cards on mobile so receptionists / owners on a phone or
 * 320px-wide tablet portrait don't have to horizontally scroll.
 */
export function ContactTimelineView({
  matched,
  events,
  summary,
}: {
  matched: ContactTimelineMatchedContact[];
  events: ContactTimelineEvent[];
  summary: ContactTimelineSummary;
}): JSX.Element {
  const hasMatch = matched.length > 0;
  const hasEvents = events.length > 0;

  if (!hasMatch && !hasEvents) {
    return <EmptyTimelineState />;
  }

  return (
    <section className="flex flex-col gap-4">
      {hasMatch ? (
        <div className="flex flex-col gap-3">
          {matched.map((c) => (
            <MatchedContactCard key={c.id} contact={c} />
          ))}
        </div>
      ) : (
        <div
          className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
          data-testid="anonymous-banner"
        >
          Anonymous activity — no contact record.
        </div>
      )}

      {hasEvents ? (
        <>
          <div className="hidden md:block">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>When</th>
                  <th>Clinic</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, i) => (
                  <EventDesktopRow
                    key={`${event.type}:${event.at}:${i}`}
                    event={event}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col gap-3 md:hidden">
            {events.map((event, i) => (
              <EventMobileCard
                key={`${event.type}:${event.at}:${i}`}
                event={event}
              />
            ))}
          </div>
        </>
      ) : null}

      <div className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">
        {summary.totalEvents} total events: {summary.contactCount} contacts,{' '}
        {summary.walkinCount} walk-ins, {summary.messageCount} messages,{' '}
        {summary.attributionCount} attribution
      </div>
    </section>
  );
}

/**
 * Animated placeholder rows shown while a phone lookup is in flight.
 * Uses the existing `animate-pulse` utility so we don't introduce
 * any new motion tokens; the bars match the eventual content height
 * so the layout doesn't jump when the real result arrives.
 */
function TimelineLoadingSkeleton(): JSX.Element {
  const rows = [0, 1, 2];
  return (
    <div
      data-testid="timeline-skeleton"
      role="status"
      aria-label="Loading timeline"
      className="flex flex-col gap-3"
    >
      <div className="h-16 animate-pulse rounded-xl bg-muted" />
      {rows.map((i) => (
        <div
          key={i}
          className="h-12 animate-pulse rounded-lg bg-muted/70"
        />
      ))}
      <span className="sr-only">Loading timeline…</span>
    </div>
  );
}

function mapErrorToCopy(message: string): string {
  const lower = message.toLowerCase();
  if (
    lower.includes('valid indian mobile') ||
    lower.includes('phone query param') ||
    lower.includes('phone must be')
  ) {
    return 'Please enter a valid Indian mobile (+91 or 10 digits starting 6-9)';
  }
  if (lower.includes('401') || lower.includes('403') || lower.includes('owner')) {
    return "You don't have permission to view this.";
  }
  if (
    lower.includes('fetch') ||
    lower.includes('network') ||
    lower.includes('failed to fetch')
  ) {
    return 'Couldn’t reach the server. Try again.';
  }
  if (lower.includes('500') || lower.includes('http 5')) {
    return 'Something went wrong. Try again or check with the team.';
  }
  return message;
}

export default function ContactTimelinePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const phoneParam = searchParams.get('phone') ?? '';
  const [inputValue, setInputValue] = useState<string>(phoneParam);
  const [result, setResult] = useState<ContactTimelineResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (phoneParam.length === 0) {
      setResult(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchOwnerContactTimeline(phoneParam)
      .then((r) => {
        if (!cancelled) {
          setResult(r);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setError(mapErrorToCopy(msg));
          setResult(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [phoneParam]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed.length === 0) return;
    setSearchParams({ phone: trimmed });
  };

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Contact timeline</h1>
        <p className="page-header__sub">
          Look up a phone number to see contacts, walk-ins, messages, and
          attribution events across your clinics.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center"
      >
        <input
          type="tel"
          inputMode="numeric"
          name="phone"
          aria-label="Phone number"
          placeholder="10-digit mobile number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:max-w-xs"
        />
        <Button type="submit" size="lg" className="h-11 px-5">
          Search
        </Button>
      </form>

      {error ? (
        <div className="mb-4">
          <DashboardLoadError message={error} />
        </div>
      ) : null}

      {loading ? <TimelineLoadingSkeleton /> : null}

      {!loading && !error && result ? (
        <ContactTimelineView
          matched={result.matchedContacts}
          events={result.events}
          summary={result.summary}
        />
      ) : null}

      {!loading && !error && result === null && phoneParam.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
          Enter a phone number above to load its timeline.
        </div>
      ) : null}
    </div>
  );
}
